import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, model } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDrawerMode, MatSidenavModule } from '@angular/material/sidenav';
import { RouterModule } from '@angular/router';

import { BaasI18n } from '@lvflanders/bar';

import { BaasActionButton } from '../../../baas-action-button/baas-action-button';
import { BaasActionButtonOptions } from '../../../baas-action-button/baas-action-button.types';
import { BaasMenuButtonItemOptions } from '../../../baas-menu-button/baas-menu-button-item.types';
import { _BaasInternalLayoutService } from '../../../layout/baas-internal-layout.service';
import { BaasExternalLinkGroupOptions } from '../../baas-external-link-group-options.type';
import { BarMenuState } from '../../bar-menu-state.type';
import { BaasLayoutService } from '../baas-layout.service';
import { BaasLayoutMenuComponent } from '../baas-layout-menu/baas-layout-menu.component';
import { BaasMenuItemOptions } from '../baas-layout-menu/baas-menu-item-options.type';
import { BaasMeerActiesComponent } from '../baas-meer-acties/baas-meer-acties.component';

@Component({
    selector: 'baas-layout-page',
    templateUrl: './baas-layout-page.component.html',
    host: { class: 'bar-layout-page-root' },
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        NgTemplateOutlet,
        RouterModule,
        MatSidenavModule,
        MatButtonModule,
        MatIconModule,
        BaasLayoutMenuComponent,
        BaasActionButton,
        BaasMeerActiesComponent
    ]
})
export class BaasLayoutPageComponent {
    readonly internalLayout = inject(_BaasInternalLayoutService);
    readonly #layout = inject(BaasLayoutService);
    readonly #i18n = inject(BaasI18n);

    readonly withRouting = input<boolean>(undefined);
    readonly menuState = model<BarMenuState>(undefined);
    readonly mobileFriendly = input<boolean>(undefined);
    readonly sidebar = input<boolean>(undefined);
    readonly _submitActions = input<BaasActionButtonOptions[]>(undefined, { alias: 'submitActions' });
    readonly moreActions = input<BaasMenuButtonItemOptions[]>(undefined);
    readonly contentActions = input<BaasActionButtonOptions[]>(undefined);
    readonly operations = input<BaasActionButtonOptions[]>(undefined);
    readonly strikingActions = input<BaasActionButtonOptions[]>(undefined);
    readonly externalLinkGroups = input<BaasExternalLinkGroupOptions[]>(undefined);
    readonly appVersion = input<string>(undefined);
    readonly mainHeader = input<string>(undefined);
    readonly menuItems = input<BaasMenuItemOptions[]>(undefined);
    readonly menuItemsAutoCollapse = input<boolean>(undefined);
    readonly hideActionBarContentLeft = input<boolean>(undefined);
    protected readonly menuAction = computed(() => {
        const translationText = this.#i18n.get(this.menuState() !== 'closed' ? 'DOMAIN_BAR_LAYOUT.MENU.CLOSE_MENU' : 'DOMAIN_BAR_LAYOUT.MENU.OPEN');
        return <BaasActionButtonOptions>{
            action: () => {
                this.menuState.set(this.menuState() !== 'closed' ? 'closed' : this.internalLayout.smallDevice() ? 'overlay' : 'pinned');
            },
            title: translationText,
            icon: {
                class: 'fas fa-bars',
                srText: translationText,
                iconOnly: true
            }
        };
    });

    protected readonly internalContentActions = this.internalLayout.internalContentActions;

    protected readonly mainAction = computed(() => this.#layout.mainAction() || this.internalLayout.mainAction());

    protected readonly sidenavMode = computed(() => {
        /* Default info: window width < 1200px equals overlay mode, > 1200px equals pinned mode */
        const stateModeMapping: { [key: string]: MatDrawerMode } = {
            closed: 'side',
            overlay: 'over',
            pinned: 'side',
            default: this.internalLayout.smallDevice() ? 'over' : 'side'
        };

        return this.menuState() ? stateModeMapping[this.menuState()] : stateModeMapping['default'];
    });
    /* based on the screen size we need to redefine the exact menu state (overlay or pinned) when in default mode */
    protected readonly menuStateExact = computed(() =>
        this.menuState() === 'default' ? (this.internalLayout.smallDevice() ? 'overlay' : 'pinned') : this.menuState());

    protected readonly submitActions = computed(() =>
        this._submitActions().map((sa) => ({ variant: 'tonal', color: 'dark', ...sa }) as BaasActionButtonOptions));

    protected handleBackdropEvent(event): void {
        this.menuState.set('closed');
    }
}
